import React, { useEffect, useMemo, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Box, Typography, CircularProgress } from "@material-ui/core";

import BusinessRoundedIcon from "@material-ui/icons/BusinessRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import AccessTimeRoundedIcon from "@material-ui/icons/AccessTimeRounded";
import TrendingUpRoundedIcon from "@material-ui/icons/TrendingUpRounded";
import BlockRoundedIcon from "@material-ui/icons/BlockRounded";
import EventBusyRoundedIcon from "@material-ui/icons/EventBusyRounded";
// MUI v4 doesn't ship SmartToy; alias the existing-in-v4 EmojiObjects
// (lightbulb-idea glyph) under the same variable name so the rest of the
// file reads as intended. Same trick ContactDrawer uses for the AI affordance.
import SmartToyRoundedIcon from "@material-ui/icons/EmojiObjects";
import ArrowForwardRoundedIcon from "@material-ui/icons/ArrowForwardRounded";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";

/**
 * Super-admin Companies-overview dashboard.
 *
 * Privacy contract: shows ONLY platform-level aggregates + tenant
 * company names. No customer emails / phones / message content /
 * billing details / API keys. A compromise of this view leaks who
 * our tenants are (which is already visible on the existing /admin
 * Companies page) and how many we have — nothing else.
 */

// === CRM design tokens (cardStackMode v3 — parity with Tags/Campaigns/Contacts) ===
const SP = { xxs: 4, xs: 8, sm: 12, md: 16, lg: 20, xl: 24, xxl: 32, hero: 48 };
const TT = { min: 44, primary: 48, hero: 56 };
const RAD = { pill: 999, sm: 8, md: 12, lg: 16, xl: 20 };

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  // CRM-canonical dark-mode-aware accent / hairline / surface vars
  const accent = isDark ? "#2ee37a" : "#128c7e";
  const accentSoft = isDark ? "rgba(46,227,122,0.08)" : "rgba(18,140,126,0.06)";
  const accentDeep = isDark ? "#7df0a8" : "#0e6c61";
  const hair = isDark ? "rgba(255,255,255,0.08)" : "rgba(15,22,20,0.06)";
  const fg = isDark ? "rgba(255,255,255,0.92)" : "#0f1614";
  const muted = isDark ? "rgba(255,255,255,0.55)" : "rgba(15,22,20,0.55)";
  const surface = isDark ? "#13181a" : "#ffffff";
  const border = hair;
  const subSurface = isDark ? "rgba(255,255,255,0.025)" : "rgba(15,22,20,0.02)";
  const cardShadow = isDark
    ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 24px rgba(0,0,0,0.32)"
    : "0 1px 0 rgba(15,22,20,0.02), 0 8px 24px rgba(15,22,20,0.04)";

  return {
    root: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: SP.xl,
      fontFamily: "Roboto, sans-serif",
    },
    // ─── Hero (page title + subtitle + accent meta) ─────────────
    hero: {
      display: "flex",
      flexDirection: "column",
      gap: 6,
      paddingBottom: SP.md,
      borderBottom: `1px solid ${hair}`,
    },
    heroAccentMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      marginBlockEnd: SP.xxs,
      display: "inline-flex",
      alignItems: "center",
      gap: SP.xs,
      // Defend the 0.22em letter-spacing token against per-character wrap
      // when a parent grid/flex column squeezes below content width.
      whiteSpace: "nowrap",
      "& svg": { fontSize: 13, flexShrink: 0 },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: SP.sm,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      minWidth: 0,
      flex: 1,
    },
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: RAD.md,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: accentSoft,
      border: `1px solid ${hair}`,
      "& .MuiSvgIcon-root": {
        fontSize: 20,
        color: accent,
      },
    },
    headerTitle: {
      fontSize: 20,
      fontWeight: 500,
      lineHeight: 1.25,
      letterSpacing: "-0.01em",
      color: fg,
      fontFamily: "Roboto, sans-serif",
      // Long localized titles need word-level break, not per-character.
      wordBreak: "break-word",
      overflowWrap: "break-word",
    },
    headerSub: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 1.45,
      color: muted,
      marginBlockStart: 2,
      fontFamily: "Roboto, sans-serif",
      wordBreak: "break-word",
      overflowWrap: "break-word",
    },
    loadingWrap: {
      display: "grid",
      placeItems: "center",
      minHeight: 320,
    },
    // ─── Section accent meta (uppercase 10.5/0.22em #128c7e) ────
    sectionAccentMeta: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      marginBlockEnd: SP.sm,
      display: "block",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    kpiGrid: {
      display: "grid",
      gap: SP.sm,
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
      // 2-up cardStackMode v3 on xs — each KPI = its own card.
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: SP.sm,
      },
    },
    kpiCard: {
      position: "relative",
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: RAD.lg,
      padding: "16px 18px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      overflow: "hidden",
      boxShadow: cardShadow,
      transition: "transform 0.18s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.18s ease, border-color 0.18s ease",
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: "var(--kpi-stripe, transparent)",
        opacity: 0.9,
      },
      "&:hover": {
        transform: "translateY(-1px)",
        borderColor: isDark ? "rgba(46,227,122,0.22)" : "rgba(18,140,126,0.18)",
        boxShadow: isDark
          ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 12px 28px rgba(0,0,0,0.36)"
          : "0 1px 0 rgba(15,22,20,0.02), 0 12px 28px rgba(15,22,20,0.06)",
      },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense: tighter padding, hairline border, near-flat shadow
        padding: "10px 12px",
        borderRadius: 8,
        border: `1px solid ${hair}`,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.24)"
          : "0 1px 2px rgba(0,0,0,0.03)",
        marginBottom: 6,
        "&:hover": {
          transform: "none",
          borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.10)",
          boxShadow: isDark
            ? "0 1px 2px rgba(0,0,0,0.28)"
            : "0 1px 2px rgba(10,10,10,0.04)",
        },
        "&::before": { display: "none" },
      },
    },
    kpiLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      // Desktop kpi card column = ~220px at 1024px shell; localized labels
      // (HE/RU/ES) at 0.22em can wrap per-character. Keep on single line,
      // ellipsize if the card is genuinely too narrow (defensive; tooltip
      // could be added later via title=).
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      "& svg": { fontSize: 13, flexShrink: 0 },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense xs: caption typography token (10/12, 0.04em uppercase)
        fontSize: 10,
        lineHeight: "12px",
        letterSpacing: "0.04em",
        fontWeight: 500,
        color: muted,
        "& svg": { fontSize: 12 },
      },
    },
    kpiValue: {
      fontSize: 28,
      fontWeight: 700,
      lineHeight: 1.05,
      color: fg,
      letterSpacing: "-0.02em",
      fontFamily: "Roboto, sans-serif",
      fontFeatureSettings: '"tnum" 1',
      // Big numbers like "₪12.5k" / large counts should stay on a single
      // line; the card has overflow:hidden so they ellipsize before they
      // can crush the kpiSub row underneath.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense xs: hero token capped at 16px (was 22, original 28)
        fontSize: 16,
        lineHeight: "20px",
        fontWeight: 600,
        letterSpacing: "-0.01em",
        color: fg,
      },
    },
    kpiSub: {
      fontSize: 12,
      fontWeight: 400,
      color: muted,
      fontFamily: "Roboto, sans-serif",
      // Body line — wrap at word, never per-character. No xs change.
      wordBreak: "break-word",
      overflowWrap: "break-word",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense body token: 12/16 muted, wrap with tight leading
        fontSize: 12,
        lineHeight: "16px",
        color: muted,
        whiteSpace: "normal",
      },
    },
    kpiAccentGreen: {
      "& $kpiValue": { color: accentDeep },
    },
    kpiAccentAmber: {
      "& $kpiValue": { color: isDark ? "#fbbf24" : "#b45309" },
    },
    kpiAccentRed: {
      "& $kpiValue": { color: isDark ? "#fca5a5" : "#b91c1c" },
    },
    // ─── Hairline section divider ───────────────────────────────
    sectionDivider: {
      height: 1,
      width: "100%",
      background: hair,
      border: "none",
      margin: 0,
    },
    twoCol: {
      display: "grid",
      gap: SP.md,
      gridTemplateColumns: "1fr 1fr",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
      },
    },
    panel: {
      position: "relative",
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: RAD.lg,
      padding: SP.lg,
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: SP.md,
      boxShadow: cardShadow,
      [theme.breakpoints.down("xs")]: {
        // Linear-dense: 12px padding, hairline border, near-flat shadow
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${hair}`,
        boxShadow: isDark
          ? "0 1px 2px rgba(0,0,0,0.24)"
          : "0 1px 2px rgba(0,0,0,0.03)",
        gap: 10,
      },
    },
    panelHead: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: SP.sm,
      paddingBlockEnd: SP.sm,
      borderBottom: `1px solid ${hair}`,
      flexWrap: "wrap",
    },
    panelTitleWrap: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
      flex: "1 1 auto",
    },
    panelEyebrow: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      // Eyebrow lives in panelHead which uses justify-content: space-between
      // with panelSub on the right; protect against per-character wrap when
      // panelSub grows from localized strings.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    },
    panelTitle: {
      fontSize: 15,
      fontWeight: 500,
      color: fg,
      letterSpacing: "-0.005em",
      fontFamily: "Roboto, sans-serif",
      wordBreak: "break-word",
      overflowWrap: "break-word",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense title token: 14/18 weight 500 fg (no truncate, wrap)
        fontSize: 14,
        lineHeight: "18px",
        fontWeight: 500,
        color: fg,
        letterSpacing: "-0.005em",
        whiteSpace: "normal",
      },
    },
    panelSub: {
      fontSize: 12,
      fontWeight: 400,
      color: muted,
      fontFamily: "Roboto, sans-serif",
      // panelHead sibling on the right; allow shrink so eyebrow keeps its
      // single-line guarantee. flex-shrink lets the wrap kick in cleanly.
      minWidth: 0,
      flexShrink: 1,
      wordBreak: "break-word",
      overflowWrap: "break-word",
      textAlign: "end",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta token: 11/14 weight 400 muted, wrap
        fontSize: 11,
        lineHeight: "14px",
        fontWeight: 400,
        color: muted,
        whiteSpace: "normal",
      },
    },
    // ─── Signups sparkline (CRM-polished mini chart) ────────────
    sparkRow: {
      display: "grid",
      gridTemplateColumns: "repeat(6, 1fr)",
      alignItems: "end",
      gap: SP.sm,
      minHeight: 120,
      paddingBlockStart: SP.xs,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, 1fr)",
        rowGap: SP.sm,
      },
    },
    sparkCol: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      minWidth: 0,
    },
    sparkBarWrap: {
      width: "100%",
      flex: 1,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      minHeight: 60,
    },
    sparkBar: {
      position: "relative",
      width: "100%",
      borderTopLeftRadius: 6,
      borderTopRightRadius: 6,
      borderBottomLeftRadius: 2,
      borderBottomRightRadius: 2,
      background: isDark
        ? "linear-gradient(180deg, rgba(46,227,122,0.85) 0%, rgba(18,140,126,0.55) 100%)"
        : "linear-gradient(180deg, #25d366 0%, #128c7e 100%)",
      minHeight: 4,
      transition: "height 320ms cubic-bezier(.2,.8,.2,1)",
      boxShadow: isDark
        ? "inset 0 1px 0 rgba(255,255,255,0.18)"
        : "inset 0 1px 0 rgba(255,255,255,0.35)",
      // Accent stripe at the top of each bar
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6,
        background: accent,
      },
    },
    sparkValue: {
      fontSize: 12,
      fontWeight: 700,
      color: fg,
      fontFeatureSettings: '"tnum" 1',
      fontFamily: "Roboto, sans-serif",
      whiteSpace: "nowrap",
    },
    sparkLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      color: accent,
      textTransform: "uppercase",
      letterSpacing: "0.22em",
      fontFamily: "Roboto, sans-serif",
      // 1/6 grid column at narrow admin shell widths can be ~80-90px; 0.22em
      // letter-spacing on localized month abbreviations causes per-character
      // wrap. Force single line, ellipsize if month abbr is unexpectedly long.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      textAlign: "center",
    },
    // ─── Plan distribution (horizontal stacked bar + list) ──────
    planStackedBar: {
      display: "flex",
      width: "100%",
      height: 10,
      borderRadius: RAD.pill,
      overflow: "hidden",
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,22,20,0.04)",
      border: `1px solid ${hair}`,
    },
    planStackedSeg: {
      height: "100%",
      transition: "width 320ms cubic-bezier(.2,.8,.2,1)",
      "&:not(:last-child)": {
        borderRight: `1px solid ${isDark ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.75)"}`,
      },
    },
    planList: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
    },
    planRow: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      paddingBlock: SP.sm,
      borderBottom: `1px solid ${hair}`,
      minWidth: 0,
      "&:last-child": { borderBottom: "none" },
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: 6,
        paddingBlock: SP.sm,
      },
    },
    planDot: {
      width: 10,
      height: 10,
      borderRadius: "50%",
      flexShrink: 0,
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
    },
    planName: {
      fontSize: 13,
      fontWeight: 500,
      color: fg,
      minWidth: 80,
      maxWidth: 140,
      fontFamily: "Roboto, sans-serif",
      // Long plan names ("Enterprise Plus", localized "Тариф Корпоративный")
      // should ellipsize, not squeeze the bar track.
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    planBarTrack: {
      flex: 1,
      minWidth: 60,
      height: 6,
      borderRadius: RAD.pill,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,22,20,0.04)",
      overflow: "hidden",
    },
    planBarFill: {
      height: "100%",
      borderRadius: RAD.pill,
      transition: "width 320ms cubic-bezier(.2,.8,.2,1)",
    },
    planMeta: {
      fontSize: 12,
      color: muted,
      minWidth: 80,
      flexShrink: 0,
      textAlign: "end",
      fontVariantNumeric: "tabular-nums",
      fontFeatureSettings: '"tnum" 1',
      fontFamily: "Roboto, sans-serif",
      whiteSpace: "nowrap",
      "& strong": { color: fg, fontWeight: 700 },
      [theme.breakpoints.down("xs")]: {
        textAlign: "start",
      },
    },
    // ─── Expiring soon list ────────────────────────────────────
    listEmpty: {
      padding: "20px 4px",
      textAlign: "center",
      fontSize: 13,
      color: muted,
      fontFamily: "Roboto, sans-serif",
    },
    expRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: SP.sm,
      padding: "12px 12px",
      borderRadius: RAD.md,
      background: subSurface,
      border: `1px solid ${hair}`,
      minWidth: 0,
      transition: "background 0.14s ease, border-color 0.14s ease",
      "&:hover": {
        background: accentSoft,
        borderColor: isDark ? "rgba(46,227,122,0.22)" : "rgba(18,140,126,0.18)",
      },
    },
    expName: {
      fontSize: 13.5,
      fontWeight: 500,
      color: fg,
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      textOverflow: "ellipsis",
      // `break-word` first prefers word boundaries; `anywhere` only kicks in
      // when a single token would still overflow.
      wordBreak: "break-word",
      overflowWrap: "anywhere",
      minWidth: 0,
      flex: 1,
      fontFamily: "Roboto, sans-serif",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense title token, no truncation
        fontSize: 14,
        lineHeight: "18px",
        fontWeight: 500,
        color: fg,
        display: "block",
        WebkitLineClamp: "unset",
        WebkitBoxOrient: "unset",
        overflow: "visible",
        textOverflow: "clip",
      },
    },
    expDays: {
      fontSize: 10.5,
      fontWeight: 700,
      padding: "4px 10px",
      borderRadius: RAD.pill,
      flexShrink: 0,
      minWidth: 64,
      textAlign: "center",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
      fontFamily: "Roboto, sans-serif",
      fontFeatureSettings: '"tnum" 1',
      [theme.breakpoints.down("xs")]: {
        // Linear-dense status pill: 2px 6px, radius 4, 10/13 weight 500
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 10,
        lineHeight: "13px",
        fontWeight: 500,
        letterSpacing: "0.04em",
      },
    },
    expDaysOk: {
      background: accentSoft,
      color: accentDeep,
      border: `1px solid ${isDark ? "rgba(46,227,122,0.18)" : "rgba(18,140,126,0.18)"}`,
    },
    expDaysWarn: {
      background: isDark ? "rgba(251,191,36,0.10)" : "rgba(251,191,36,0.14)",
      color: isDark ? "#fbbf24" : "#b45309",
      border: `1px solid ${isDark ? "rgba(251,191,36,0.22)" : "rgba(180,83,9,0.22)"}`,
    },
    expDaysCrit: {
      background: isDark ? "rgba(252,165,165,0.10)" : "rgba(252,165,165,0.18)",
      color: isDark ? "#fca5a5" : "#b91c1c",
      border: `1px solid ${isDark ? "rgba(252,165,165,0.22)" : "rgba(185,28,28,0.22)"}`,
    },
    // ─── Needs-attention pills ─────────────────────────────────
    attentionGrid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: 0,
    },
    attentionRow: {
      display: "flex",
      alignItems: "center",
      gap: SP.sm,
      padding: "12px 4px",
      borderBottom: `1px solid ${hair}`,
      transition: "background 0.14s ease",
      "&:last-of-type": {
        borderBottom: "none",
      },
    },
    attentionRowClickable: {
      cursor: "pointer",
      marginInline: -SP.xs,
      paddingInline: SP.xs,
      borderRadius: RAD.md,
      "&:hover": {
        background: accentSoft,
      },
    },
    attentionIcon: {
      width: 36,
      height: 36,
      borderRadius: RAD.md,
      display: "grid",
      placeItems: "center",
      flexShrink: 0,
      border: `1px solid ${hair}`,
    },
    attentionLabel: {
      fontSize: 13.5,
      fontWeight: 500,
      color: fg,
      flex: 1,
      minWidth: 0,
      wordBreak: "break-word",
      overflowWrap: "break-word",
      fontFamily: "Roboto, sans-serif",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense title token, wrap not truncate
        fontSize: 13,
        lineHeight: "17px",
        fontWeight: 500,
        color: fg,
        whiteSpace: "normal",
      },
    },
    attentionCount: {
      fontSize: 18,
      fontWeight: 700,
      color: fg,
      fontVariantNumeric: "tabular-nums",
      fontFeatureSettings: '"tnum" 1',
      letterSpacing: "-0.01em",
      whiteSpace: "nowrap",
      flexShrink: 0,
      fontFamily: "Roboto, sans-serif",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense hero count token capped at 16px
        fontSize: 16,
        lineHeight: "20px",
        fontWeight: 600,
        color: fg,
      },
    },
    openLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: "transparent",
      border: "none",
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color: accent,
      cursor: "pointer",
      padding: 0,
      minHeight: TT.min,
      // Action label sits in panelHead (justify-content: space-between);
      // keep it single-line so the 0.22em letter-spacing token never wraps.
      whiteSpace: "nowrap",
      flexShrink: 0,
      fontFamily: "Roboto, sans-serif",
      "& svg": { fontSize: 14, flexShrink: 0 },
      "&:hover": { textDecoration: "underline" },
      [theme.breakpoints.down("xs")]: {
        minHeight: TT.min,
      },
    },
    // ─── Recent activity widget ──────────────────────────────
    activityList: {
      display: "flex",
      flexDirection: "column",
      gap: 0,
    },
    activityRow: {
      display: "flex",
      alignItems: "flex-start",
      gap: SP.sm,
      padding: "12px 0",
      borderBottom: `1px solid ${hair}`,
      "&:last-child": { borderBottom: "none" },
    },
    activityDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
      marginBlockStart: 7,
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
    },
    activityBody: {
      flex: 1,
      minWidth: 0,
    },
    activityLabel: {
      fontSize: 13,
      fontWeight: 400,
      color: fg,
      lineHeight: 1.45,
      fontFamily: "Roboto, sans-serif",
      // Tenant names + admin names can be long localized strings; word-break
      // (not per-character) keeps the body readable.
      wordBreak: "break-word",
      overflowWrap: "break-word",
      "& strong": { fontWeight: 700 },
      [theme.breakpoints.down("xs")]: {
        // Linear-dense body token: 12/16 weight 400 fg, wrap
        fontSize: 12,
        lineHeight: "16px",
        fontWeight: 400,
        color: fg,
        whiteSpace: "normal",
        wordBreak: "break-word",
        "& strong": { fontWeight: 600 },
      },
    },
    activityTime: {
      fontSize: 10.5,
      fontWeight: 700,
      color: accent,
      letterSpacing: "0.18em",
      textTransform: "uppercase",
      marginBlockStart: 4,
      fontVariantNumeric: "tabular-nums",
      fontFeatureSettings: '"tnum" 1',
      fontFamily: "Roboto, sans-serif",
      whiteSpace: "nowrap",
      [theme.breakpoints.down("xs")]: {
        // Linear-dense meta token: 10/12 caption, muted (no accent on chrome)
        fontSize: 10,
        lineHeight: "12px",
        letterSpacing: "0.04em",
        fontWeight: 500,
        color: muted,
        textTransform: "uppercase",
      },
    },
  };
});

// Plan tier colors — same hues as CompaniesManager for consistency.
const PLAN_COLOR = (name) => {
  const lower = String(name || "").toLowerCase();
  if (lower.includes("enterprise")) return "#7c3aed";
  if (lower.includes("pro")) return "#0ea5e9";
  if (lower.includes("basic") || lower.includes("starter")) return "#10b981";
  if (lower === "no plan") return "#94a3b8";
  return "#25d366";
};

const formatMoney = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const symbol =
    currency === "ILS" ? "₪" :
    currency === "USD" ? "$" :
    currency === "EUR" ? "€" : "";
  if (n >= 1000) {
    return `${symbol}${(n / 1000).toFixed(1)}k`;
  }
  return `${symbol}${Math.round(n).toLocaleString()}`;
};

const formatMonthShort = (yyyymm) => {
  // "2026-05" → "May" (en) or i18n month abbr if available
  try {
    const [, m] = yyyymm.split("-");
    const idx = parseInt(m, 10) - 1;
    const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                   "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return names[idx] || yyyymm;
  } catch {
    return yyyymm;
  }
};

const AdminOverview = ({ onOpenCompanies, onOpenCompaniesFiltered, onOpenAuditLog }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDarkMode = theme.palette.type === "dark";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  // Recent activity is a separate, cheaper call to /audit-log so the
  // main /dashboard payload doesn't have to lug an 8-row admin log
  // along for every Overview render. Loaded in parallel; failure is
  // soft (widget shows empty/skeleton, dashboard still renders).
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/api/admin/dashboard")
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError(
          i18n.t("admin.overview.loadError", {
            defaultValue: "Could not load overview. Try refreshing.",
          })
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Recent platform-operator activity — parallel fetch, soft-fail.
  useEffect(() => {
    let cancelled = false;
    setActivityLoading(true);
    api
      .get("/api/admin/audit-log", { params: { page: 1, limit: 8 } })
      .then((res) => {
        if (!cancelled) setActivity(res.data?.rows || []);
      })
      .catch(() => {
        if (!cancelled) setActivity([]);
      })
      .finally(() => {
        if (!cancelled) setActivityLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const sparkMax = useMemo(() => {
    if (!data?.signups?.length) return 1;
    return Math.max(1, ...data.signups.map((s) => s.count));
  }, [data]);

  if (loading) {
    return (
      <Box className={classes.loadingWrap}>
        <CircularProgress size={28} style={{ color: "#128c7e" }} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box className={classes.loadingWrap}>
        <Typography variant="body2" color="textSecondary">
          {error || i18n.t("admin.overview.empty", { defaultValue: "No data yet." })}
        </Typography>
      </Box>
    );
  }

  const { totals, signups, planDistribution, expiringSoon, needsAttention } = data;

  return (
    <Box className={classes.root}>
      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box className={classes.hero}>
        <span className={classes.heroAccentMeta}>
          <BusinessRoundedIcon />
          {i18n.t("admin.overview.eyebrow", { defaultValue: "OVERVIEW" })}
        </span>
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <span className={classes.headerIcon}>
              <BusinessRoundedIcon />
            </span>
            <Box>
              <Typography className={classes.headerTitle} component="div">
                {i18n.t("admin.overview.title", { defaultValue: "Companies overview" })}
              </Typography>
              <Typography className={classes.headerSub}>
                {i18n.t("admin.overview.subtitle", {
                  defaultValue: "Platform-level metrics across all tenants",
                })}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── KPI cards (cardStackMode v3) ──────────────────────── */}
      <Box className={classes.kpiGrid}>
        <Box
          className={classes.kpiCard}
          style={{ "--kpi-stripe": isDarkMode ? "#2ee37a" : "#128c7e" }}
        >
          <span className={classes.kpiLabel}>
            <BusinessRoundedIcon />
            {i18n.t("admin.overview.kpi.companies", { defaultValue: "Companies" })}
          </span>
          <Typography className={classes.kpiValue}>{totals.companies}</Typography>
          <Typography className={classes.kpiSub}>
            {i18n.t("admin.overview.kpi.newThisMonth", {
              count: totals.newThisMonth,
              defaultValue: `+${totals.newThisMonth} this month`,
            })}
          </Typography>
        </Box>

        <Box
          className={`${classes.kpiCard} ${classes.kpiAccentGreen}`}
          style={{ "--kpi-stripe": "#25d366" }}
        >
          <span className={classes.kpiLabel}>
            <CheckCircleRoundedIcon />
            {i18n.t("admin.overview.kpi.active", { defaultValue: "Active" })}
          </span>
          <Typography className={classes.kpiValue}>{totals.active}</Typography>
          <Typography className={classes.kpiSub}>
            {totals.companies > 0
              ? `${Math.round((totals.active / totals.companies) * 100)}%`
              : "—"}
          </Typography>
        </Box>

        <Box
          className={`${classes.kpiCard} ${classes.kpiAccentAmber}`}
          style={{ "--kpi-stripe": "#f59e0b" }}
        >
          <span className={classes.kpiLabel}>
            <AccessTimeRoundedIcon />
            {i18n.t("admin.overview.kpi.expiringSoon", {
              defaultValue: "Expiring soon",
            })}
          </span>
          <Typography className={classes.kpiValue}>{totals.expiringSoon}</Typography>
          <Typography className={classes.kpiSub}>
            {i18n.t("admin.overview.kpi.within7Days", { defaultValue: "Within 7 days" })}
          </Typography>
        </Box>

        <Box
          className={classes.kpiCard}
          style={{ "--kpi-stripe": isDarkMode ? "#2ee37a" : "#128c7e" }}
        >
          <span className={classes.kpiLabel}>
            <TrendingUpRoundedIcon />
            {i18n.t("admin.overview.kpi.mrr", { defaultValue: "MRR (est.)" })}
          </span>
          <Typography className={classes.kpiValue}>
            {formatMoney(totals.mrrEstimate, totals.mrrCurrency)}
          </Typography>
          <Typography className={classes.kpiSub}>
            {i18n.t("admin.overview.kpi.mrrSub", {
              defaultValue: "Monthly equivalent",
            })}
          </Typography>
        </Box>
      </Box>

      {/* ── Signups + plan distribution ───────────────────────── */}
      <Box className={classes.twoCol}>
        <Box className={classes.panel}>
          <Box className={classes.panelHead}>
            <Box className={classes.panelTitleWrap}>
              <span className={classes.panelEyebrow}>
                {i18n.t("admin.overview.signups.eyebrow", { defaultValue: "TREND" })}
              </span>
              <Typography className={classes.panelTitle}>
                {i18n.t("admin.overview.signups.title", {
                  defaultValue: "Signups (last 6 months)",
                })}
              </Typography>
            </Box>
            <Typography className={classes.panelSub}>
              {i18n.t("admin.overview.signups.sub", {
                defaultValue: "New companies per month",
              })}
            </Typography>
          </Box>
          <Box className={classes.sparkRow}>
            {signups.map((s) => {
              const heightPct = Math.max(6, Math.round((s.count / sparkMax) * 100));
              return (
                <Box key={s.month} className={classes.sparkCol}>
                  <Typography className={classes.sparkValue}>{s.count}</Typography>
                  <Box className={classes.sparkBarWrap}>
                    <Box
                      className={classes.sparkBar}
                      style={{ height: `${heightPct}%` }}
                    />
                  </Box>
                  <Typography className={classes.sparkLabel}>
                    {formatMonthShort(s.month)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box className={classes.panel}>
          <Box className={classes.panelHead}>
            <Box className={classes.panelTitleWrap}>
              <span className={classes.panelEyebrow}>
                {i18n.t("admin.overview.plans.eyebrow", { defaultValue: "TIERS" })}
              </span>
              <Typography className={classes.panelTitle}>
                {i18n.t("admin.overview.plans.title", {
                  defaultValue: "Plan distribution",
                })}
              </Typography>
            </Box>
            <Typography className={classes.panelSub}>
              {i18n.t("admin.overview.plans.sub", {
                defaultValue: "Share of companies per tier",
              })}
            </Typography>
          </Box>
          {planDistribution.length === 0 ? (
            <Box className={classes.listEmpty}>
              {i18n.t("admin.overview.empty", { defaultValue: "No data yet." })}
            </Box>
          ) : (
            <>
              <Box className={classes.planStackedBar}>
                {planDistribution.map((p) => (
                  <Box
                    key={`stack-${p.planId || "none"}-${p.name}`}
                    className={classes.planStackedSeg}
                    style={{
                      width: `${Math.max(0, Math.min(100, p.pct))}%`,
                      background: PLAN_COLOR(p.name),
                    }}
                  />
                ))}
              </Box>
              <Box className={classes.planList}>
                {planDistribution.map((p) => {
                  const color = PLAN_COLOR(p.name);
                  return (
                    <Box key={`${p.planId || "none"}-${p.name}`} className={classes.planRow}>
                      <span className={classes.planDot} style={{ background: color }} />
                      <Typography className={classes.planName}>{p.name}</Typography>
                      <Box className={classes.planBarTrack}>
                        <Box
                          className={classes.planBarFill}
                          style={{ width: `${Math.min(100, p.pct)}%`, background: color }}
                        />
                      </Box>
                      <Typography className={classes.planMeta}>
                        <strong>{p.count}</strong> · {p.pct}%
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* ── Expiring soon list + needs attention ──────────────── */}
      <Box className={classes.twoCol}>
        <Box className={classes.panel}>
          <Box className={classes.panelHead}>
            <Box className={classes.panelTitleWrap}>
              <span className={classes.panelEyebrow}>
                {i18n.t("admin.overview.expiring.eyebrow", { defaultValue: "RENEWALS" })}
              </span>
              <Typography className={classes.panelTitle}>
                {i18n.t("admin.overview.expiring.title", {
                  defaultValue: "Expiring soon",
                })}
              </Typography>
            </Box>
            {onOpenCompanies && expiringSoon.length > 0 && (
              <button type="button" className={classes.openLink} onClick={onOpenCompanies}>
                {i18n.t("admin.overview.expiring.viewAll", { defaultValue: "View all" })}
                <ArrowForwardRoundedIcon />
              </button>
            )}
          </Box>
          {expiringSoon.length === 0 ? (
            <Box className={classes.listEmpty}>
              {i18n.t("admin.overview.expiring.empty", {
                defaultValue: "No expirations in the next 7 days.",
              })}
            </Box>
          ) : (
            <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {expiringSoon.map((c) => {
                const cls =
                  c.daysUntil <= 1 ? classes.expDaysCrit :
                  c.daysUntil <= 3 ? classes.expDaysWarn :
                  classes.expDaysOk;
                return (
                  <Box key={c.id} className={classes.expRow}>
                    <Typography className={classes.expName}>{c.name}</Typography>
                    <span className={`${classes.expDays} ${cls}`}>
                      {c.daysUntil === 0
                        ? i18n.t("admin.overview.expiring.today", { defaultValue: "today" })
                        : c.daysUntil === 1
                          ? i18n.t("admin.overview.expiring.tomorrow", { defaultValue: "tomorrow" })
                          : i18n.t("admin.overview.expiring.inDays", {
                              days: c.daysUntil,
                              defaultValue: `in ${c.daysUntil} days`,
                            })}
                    </span>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Box className={classes.panel}>
          <Box className={classes.panelHead}>
            <Box className={classes.panelTitleWrap}>
              <span className={classes.panelEyebrow}>
                {i18n.t("admin.overview.attention.eyebrow", { defaultValue: "ALERTS" })}
              </span>
              <Typography className={classes.panelTitle}>
                {i18n.t("admin.overview.attention.title", {
                  defaultValue: "Needs attention",
                })}
              </Typography>
            </Box>
            <Typography className={classes.panelSub}>
              {i18n.t("admin.overview.attention.sub", {
                defaultValue: "Counts only — open Companies to act",
              })}
            </Typography>
          </Box>
          <Box className={classes.attentionGrid}>
            <Box
              className={`${classes.attentionRow} ${
                needsAttention.suspended > 0 && onOpenCompaniesFiltered
                  ? classes.attentionRowClickable
                  : ""
              }`}
              role={needsAttention.suspended > 0 && onOpenCompaniesFiltered ? "button" : undefined}
              tabIndex={needsAttention.suspended > 0 && onOpenCompaniesFiltered ? 0 : undefined}
              onClick={() => {
                if (needsAttention.suspended > 0 && onOpenCompaniesFiltered) {
                  onOpenCompaniesFiltered("suspended");
                }
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  needsAttention.suspended > 0 &&
                  onOpenCompaniesFiltered
                ) {
                  onOpenCompaniesFiltered("suspended");
                }
              }}
            >
              <span
                className={classes.attentionIcon}
                style={{ background: "rgba(248,113,113,0.16)", color: "#f87171" }}
              >
                <BlockRoundedIcon style={{ fontSize: 18 }} />
              </span>
              <Typography className={classes.attentionLabel}>
                {i18n.t("admin.overview.attention.suspended", { defaultValue: "Suspended" })}
              </Typography>
              <Typography className={classes.attentionCount}>
                {needsAttention.suspended}
              </Typography>
            </Box>

            <Box
              className={`${classes.attentionRow} ${
                needsAttention.pastDue > 0 && onOpenCompaniesFiltered
                  ? classes.attentionRowClickable
                  : ""
              }`}
              role={needsAttention.pastDue > 0 && onOpenCompaniesFiltered ? "button" : undefined}
              tabIndex={needsAttention.pastDue > 0 && onOpenCompaniesFiltered ? 0 : undefined}
              onClick={() => {
                if (needsAttention.pastDue > 0 && onOpenCompaniesFiltered) {
                  onOpenCompaniesFiltered("past_due");
                }
              }}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.key === " ") &&
                  needsAttention.pastDue > 0 &&
                  onOpenCompaniesFiltered
                ) {
                  onOpenCompaniesFiltered("past_due");
                }
              }}
            >
              <span
                className={classes.attentionIcon}
                style={{ background: "rgba(251,191,36,0.16)", color: "#fbbf24" }}
              >
                <EventBusyRoundedIcon style={{ fontSize: 18 }} />
              </span>
              <Typography className={classes.attentionLabel}>
                {i18n.t("admin.overview.attention.pastDue", { defaultValue: "Past due date" })}
              </Typography>
              <Typography className={classes.attentionCount}>
                {needsAttention.pastDue}
              </Typography>
            </Box>

            {/* AI-off stays static for now — the dashboard payload has the
                count but doesn't include the company-id list, and CompaniesManager
                doesn't yet read aiEnabled per row. Wire up when the filter exists. */}
            <Box className={classes.attentionRow}>
              <span
                className={classes.attentionIcon}
                style={{ background: "rgba(148,163,184,0.16)", color: "#94a3b8" }}
              >
                <SmartToyRoundedIcon style={{ fontSize: 18 }} />
              </span>
              <Typography className={classes.attentionLabel}>
                {i18n.t("admin.overview.attention.aiOff", { defaultValue: "AI disabled" })}
              </Typography>
              <Typography className={classes.attentionCount}>
                {needsAttention.aiOff}
              </Typography>
            </Box>

            {onOpenCompanies && (
              <button
                type="button"
                className={classes.openLink}
                onClick={onOpenCompanies}
                style={{ marginTop: 4, alignSelf: "flex-end" }}
              >
                {i18n.t("admin.overview.attention.openCompanies", {
                  defaultValue: "Open Companies",
                })}
                <ArrowForwardRoundedIcon />
              </button>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Recent platform activity (last 8 admin actions) ─── */}
      <Box className={classes.panel}>
        <Box className={classes.panelHead}>
          <Box className={classes.panelTitleWrap}>
            <span className={classes.panelEyebrow}>
              {i18n.t("admin.overview.activity.eyebrow", { defaultValue: "AUDIT" })}
            </span>
            <Typography className={classes.panelTitle}>
              {i18n.t("admin.overview.activity.title", {
                defaultValue: "Recent platform activity",
              })}
            </Typography>
          </Box>
          {onOpenAuditLog && activity.length > 0 && (
            <button
              type="button"
              className={classes.openLink}
              onClick={onOpenAuditLog}
            >
              {i18n.t("admin.overview.activity.viewAll", {
                defaultValue: "View full audit log",
              })}
              <ArrowForwardRoundedIcon />
            </button>
          )}
        </Box>
        {activityLoading ? (
          <Box className={classes.listEmpty}>…</Box>
        ) : activity.length === 0 ? (
          <Box className={classes.listEmpty}>
            {i18n.t("admin.overview.activity.empty", {
              defaultValue: "No platform actions recorded yet.",
            })}
          </Box>
        ) : (
          <Box className={classes.activityList}>
            {activity.map((row) => {
              const tone =
                row.action === "suspend_tenant" ? "#b91c1c" :
                row.action === "ai_disable" ? "#94a3b8" :
                "#25d366";
              const adminName = row.adminUser?.name || `admin #${row.adminUser?.id || "?"}`;
              const targetName = row.targetCompany?.name || "—";
              const actionLabel = i18n.t(`admin.health.action.${row.action}`, {
                defaultValue: row.action,
              });
              return (
                <Box key={row.id} className={classes.activityRow}>
                  <span className={classes.activityDot} style={{ background: tone }} />
                  <Box className={classes.activityBody}>
                    <Typography className={classes.activityLabel} component="div">
                      <strong>{adminName}</strong> · {actionLabel}
                      {row.targetCompany && (
                        <>
                          {" → "}
                          <strong>{targetName}</strong>
                        </>
                      )}
                    </Typography>
                    <Typography className={classes.activityTime}>
                      {formatRelativeAgo(row.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Compact "5m ago" / "2h" / "3d" string used in the activity widget.
// Kept local because formatRelativeTime in TenantHealthPanel has slightly
// different rules (we want "5m ago" with the suffix here, not just "5m").
function formatRelativeAgo(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return "—";
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return i18n.t("admin.overview.activity.now", { defaultValue: "just now" });
  if (mins < 60) {
    return i18n.t("admin.overview.activity.minAgo", {
      count: mins,
      defaultValue: `${mins}m ago`,
    });
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return i18n.t("admin.overview.activity.hAgo", {
      count: hrs,
      defaultValue: `${hrs}h ago`,
    });
  }
  const days = Math.floor(hrs / 24);
  return i18n.t("admin.overview.activity.dAgo", {
    count: days,
    defaultValue: `${days}d ago`,
  });
}

export default AdminOverview;
